"use client"

import { createContext, useCallback, useContext, useMemo, useState } from "react"
import { useBookmarks } from "../hooks/useBookmarks"
import type { Bookmark } from "../types/bookmark.types"

interface BookmarksContextValue {
  bookmarks: Bookmark[]
  bookmarkedIds: Set<string>
  isLoading: boolean
  refetch: () => Promise<void>
  optimisticToggle: (entityId: string, bookmarked: boolean) => void
}

const BookmarksContext = createContext<BookmarksContextValue | null>(null)

interface BookmarksProviderProps {
  children: React.ReactNode
  userEmail: string | null
}

/**
 * Provider that loads ALL user bookmarks once
 * and provides a Set of bookmarked IDs for fast lookup
 * with optimistic updates
 */
export function BookmarksProvider({
  children,
  userEmail,
}: BookmarksProviderProps) {
  const { bookmarks, isLoading, refetch } = useBookmarks({
    userEmail: userEmail || undefined,
    enabled: !!userEmail,
  })

  // Local optimistic state for bookmarked IDs
  const [optimisticIds, setOptimisticIds] = useState<Set<string>>(new Set())

  // Create a Set of bookmarked entity IDs for O(1) lookup
  // Merge server data with optimistic updates
  const bookmarkedIds = useMemo(() => {
    const serverIds = new Set(bookmarks.map((b) => b.entityId))
    // Apply optimistic changes
    const merged = new Set(serverIds)
    optimisticIds.forEach((id) => {
      if (serverIds.has(id)) {
        merged.delete(id) // Optimistic remove
      } else {
        merged.add(id) // Optimistic add
      }
    })
    return merged
  }, [bookmarks, optimisticIds])

  /**
   * Optimistic toggle - updates UI immediately without refetch
   * Only the toggled button updates, others stay unchanged
   */
  const optimisticToggle = useCallback((entityId: string, bookmarked: boolean) => {
    setOptimisticIds((prev) => {
      const next = new Set(prev)
      if (bookmarked) {
        next.add(entityId)
      } else {
        next.delete(entityId)
      }
      return next
    })
  }, [])

  const value: BookmarksContextValue = {
    bookmarks,
    bookmarkedIds,
    isLoading,
    refetch,
    optimisticToggle,
  }

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>
  )
}

/**
 * Hook to access bookmarks context
 */
export function useBookmarksContext() {
  const context = useContext(BookmarksContext)
  if (!context) {
    throw new Error(
      "useBookmarksContext must be used within BookmarksProvider"
    )
  }
  return context
}

/**
 * Hook to check if a specific entity is bookmarked
 * O(1) lookup in memory - no API call
 */
export function useIsBookmarked(entityId: string): boolean {
  const { bookmarkedIds, isLoading } = useBookmarksContext()
  if (isLoading) return false
  return bookmarkedIds.has(entityId)
}
